/* ConfigureAuditScreenRenderer.js */

"use strict";
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const InputTextControl = require("@gluenetworks/steve_ui_core").Control.Textfield.InputText;
const CheckboxControl = require("@gluenetworks/steve_ui_core").Control.Boolean.InputCheckbox;
const InputRadioControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single.InputRadio;
const ComboBoxControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single.ComboBox;

class ConfigureAuditScreenRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("configure-audit-screen-gl");
        this._rootElem.component = this._model;
        this._logger = this._model.logger;

        this._titleText = document.createElement("h3");
        this._titleText.innerHTML = "Audit Configuration";

        this._auditTitleInput = new InputTextControl({
            darkTheme: true,
            value: "Audit Run - " + new Date().toLocaleDateString() + " " + new Date().toLocaleTimeString()
        });

        this._devicesContainer = document.createElement("div");
        this._devicesContainer.classList.add("devices-container");
        this._devicesLabel = document.createElement("label");
        this._devicesLabel.innerHTML = "Devices:";
        this._devices = new InputRadioControl({
            darkTheme: true,
            horizontal: true,
            disabled: true,
            options: [
                {
                    label: "Selection",
                    value: "selection"
                },
                {
                    label: "Apply Current Filter",
                    value: "apply_current_filter",
                    disabled: true
                },
                {
                    label: "All",
                    value: "all",
                    disabled: true
                }
            ],
            value: "selection"
        });
        this._captureNewSnapshotContainer = document.createElement("div");
        this._captureNewSnapshotContainer.classList.add("capture-snapshort-container");
        this._captureNewSnapshot = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Capture New Snapshot",
            value: this._model.isCaptureSnapshotActive
        });

        this._captureNewSnapshotInput = new InputTextControl({
            darkTheme: true,
            placeholder: "Enter a name for this Snapshot (optional)",
            disabled: !this._model.isCaptureSnapshotActive || this._model._runAll
        });

        this._auditPolicyContainer = document.createElement("div");
        this._auditPolicyContainer.classList.add("audit-policy-container");

        this._auditPolicyLabel = document.createElement("label");
        this._auditPolicyLabel.innerHTML = "Policy: ";
        this._auditPolicy = new ComboBoxControl({ options: [], disabled: true });

        this._buttonBarElem = document.createElement("div");
        this._buttonBarElem.classList.add("button-bar");

        this._cancelButton = new ButtonControl({
            label: "Cancel",
            darkTheme: true
        });

        this._scheduleAudit = new ButtonControl({
            label: "Schedule Audit",
            darkTheme: true,
            disabled: false
        });

        this._startAudit = new ButtonControl({
            label: "Start Audit",
            darkTheme: true,
            disabled: true
        });

        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._captureNewSnapshot.on("UserValue", this._toggleCaptureNewSnapshotAvailabilityAction.bind(this));
        this._auditPolicy.on("UserValue", (this._toggleauditPolicyAction = this._toggleauditPolicyAction.bind(this)));
        this._scheduleAudit.on("UserClick", this._scheduleAuditAction.bind(this));
        this._startAudit.on("UserClick", this._startAuditAction.bind(this));
        this._cancelButton.on("UserClick", (this._onCancelButtonClick = this._onCancelButtonClick.bind(this)));
    }

    _unregisterListeners() {
        this._captureNewSnapshot.removeListener("UserValue", this._toggleCaptureNewSnapshotAvailabilityAction);
        this._auditPolicy.removeListener("UserValue", this._toggleauditPolicyAction);
        this._scheduleAudit.removeListener("UserClick", this._scheduleAuditAction);
        this._startAudit.on("UserClick", this._startAuditAction);
        this._cancelButton.removeAllListeners("UserClick");
    }

    _disableViewActions() {
        this._scheduleAudit.setDisabled(true);
        this._startAudit.setDisabled(true);
    }

    _enableViewActions() {
        this._scheduleAudit.setDisabled(false);
        this._startAudit.setDisabled(false);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();

        let promArr = [
            this._auditTitleInput.shutdown(),
            this._devices.shutdown(),
            this._captureNewSnapshot.shutdown(),
            this._captureNewSnapshotInput.shutdown(),
            this._auditPolicy.shutdown(),
            this._scheduleAudit.shutdown(),
            this._startAudit.shutdown(),
            this._cancelButton.shutdown()
        ];

        return Promise.all(promArr).then(
            () => {
                this._titleText.remove();
                this._titleText = undefined;
                this._auditTitleInput = undefined;
                this._devicesLabel.remove();
                this._devicesLabel = undefined;
                this._devices = undefined;
                this._devicesContainer.remove();
                this._devicesContainer = undefined;
                this._captureNewSnapshotContainer.remove();
                this._captureNewSnapshotContainer = undefined;
                this._captureNewSnapshot = undefined;
                this._captureNewSnapshotInput = undefined;
                this._auditPolicyLabel.remove();
                this._auditPolicyLabel = undefined;
                this._auditPolicy = undefined;
                this._auditPolicyContainer.remove();
                this._auditPolicyContainer = undefined;
                this._buttonBarElem.remove();
                this._scheduleAudit = undefined;
                this._cancelButton = undefined;
                this._startAudit = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this._logger.warn({ error: err }, "ConfigureAuditScreenRenderer.shutdown() error.");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return this._model
            .whenModelIsReady()
            .then(() => {
                if (!this._rootElem.hasChildNodes()) {
                    this._rootElem.appendChild(this._titleText);
                    this._rootElem.appendChild(this._auditTitleInput.renderer.rootElement);

                    this._devicesContainer.appendChild(this._devicesLabel);
                    this._devicesContainer.appendChild(this._devices.renderer.rootElement);
                    this._rootElem.appendChild(this._devicesContainer);

                    this._captureNewSnapshotContainer.appendChild(this._captureNewSnapshot.renderer.rootElement);
                    this._captureNewSnapshotContainer.appendChild(this._captureNewSnapshotInput.renderer.rootElement);
                    this._rootElem.appendChild(this._captureNewSnapshotContainer);
                    if (this._model._labelValues.length === 1) {
                        this._auditPolicy.setOptions(this._model._labelValues);
                        this._auditPolicyContainer.appendChild(this._auditPolicyLabel);
                        this._auditPolicyContainer.appendChild(this._auditPolicy.renderer.rootElement);
                        this._auditPolicy.setValue(this._model._labelValues[0].value);
                        if (!this._model._runAll) {
                            this._rootElem.appendChild(this._auditPolicyContainer);
                        }
                    }
                    this._enableViewActions();

                    this._buttonBarElem.appendChild(this._cancelButton.renderer.rootElement);
                    this._buttonBarElem.appendChild(this._scheduleAudit.renderer.rootElement);
                    this._buttonBarElem.appendChild(this._startAudit.renderer.rootElement);
                    this._rootElem.appendChild(this._buttonBarElem);
                }
                Promise.resolve();
            })
            .catch((err) => {
                return this._model._modalUserFeedback.displayError(err);
            });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _toggleCaptureNewSnapshotAvailabilityAction(newValue) {
        if (newValue !== this._model.isCaptureSnapshotActive) {
            this._model.isCaptureSnapshotActive = newValue;
            this._captureNewSnapshotInput.setDisabled(!newValue);
            // if(this._model._runAll) {
            // this._captureNewSnapshotInput.setDisabled(true);
            // }
        }
    }
    _toggleauditPolicyAction(newValue) {
        if (newValue) {
            this._enableViewActions();
        } else {
            this._disableViewActions();
        }
    }
    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _onCancelButtonClick() {
        let err = new Error("user rejected");
        err.userRejected = true;
        this.model.cancelAction(err);
    }

    _scheduleAuditAction() {
        const selection = this._devices.value,
            workInput = this._getWorkInput();

        return this._model._scheduleConfigAuditWork(selection, workInput);
    }

    _startAuditAction() {
        const selection = this._devices.value,
            workInput = this._getWorkInput();
        return this._model._startConfigAuditWork(selection, workInput);
    }

    _getWorkInput() {
        const workInp = {
            auditTitle: this._auditTitleInput.value,
            captureNewSnapShots: this._captureNewSnapshot.value,
            snapshotName: this._captureNewSnapshot.value ? this._captureNewSnapshotInput.value : undefined
        };
        if (this._model._runAll) {
            workInp.policyIds = this._model._policyIds;
        } else {
            workInp.policyId = this._auditPolicy.getValue();
        }
        return workInp;
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = ConfigureAuditScreenRenderer;
